'use client';

import { useEffect } from 'react';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { usePathname } from 'next/navigation';

import logger from '@/shared/libs/logger.client';

/**
 * L'écran qui remplace la page blanche (#1567).
 *
 * Sans lui, **une seule exception de rendu laissait l'application vide** : pas de message, pas de
 * bouton, rien à faire d'autre que deviner qu'il fallait recharger. C'est arrivé sur le tableau de
 * bord juste après la connexion — le premier écran que voit un client, sur l'espace payant.
 *
 * Le correctif de la cause est ailleurs ; celui-ci vaut pour **toutes les prochaines**, qui
 * produiraient sinon exactement la même page blanche.
 *
 * Next appelle ce composant pour toute erreur levée dans l'arbre en dessous. `reset()` retente le
 * rendu sans recharger la page : c'est le geste le moins coûteux, et il suffit quand l'erreur tenait
 * à un état transitoire — ce qui est précisément le cas ici.
 */
export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  /*
    **Le chemin de la page, avec la pile** (#1719).

    La pile dit où le code a levé ; elle ne dit pas ce que la personne regardait. Sur un défaut qui
    ne se produit que sur un écran — et celui de #1719 ne se produisait que sur la liste des
    missions —, c'est la première question qu'on se pose, et elle n'était nulle part.
  */
  const pathname = usePathname();

  useEffect(() => {
    /*
      Journalisé côté client : sans cela, l'incident ne laisse aucune trace ailleurs que dans la
      console de celui qui l'a subi — et personne ne la lit à sa place.
    */
    logger.error('[AppError] Rendu interrompu', {
      pathname,
      message: error.message,
      digest: error.digest,
      stack: error.stack,
    });
  }, [error, pathname]);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
          <TriangleAlert className="size-6" />
        </span>
        <h1 className="text-text-strong-950 mt-4 text-base font-semibold">
          L’affichage s’est interrompu
        </h1>
        {/*
          Ce qu'on sait, et ce qu'on ne sait pas. Promettre « une erreur est survenue, réessayez
          plus tard » sur un incident que `reset()` répare en une seconde ferait fermer l'onglet.
        */}
        <p className="text-text-sub-600 mt-2 text-sm leading-relaxed">
          Vos données n’ont pas été perdues. Réessayez : l’écran se reconstruit sans recharger la
          page.
        </p>
        <button
          type="button"
          onClick={reset}
          className="from-primary-500 to-primary-700 mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r py-3 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          <RefreshCw className="size-4" />
          Réessayer
        </button>
        {/*
          L'identifiant technique, discret mais présent : c'est la seule chose qui relie ce que
          l'utilisateur a vu à ce que le journal a enregistré.
        */}
        {error.digest && (
          <p className="text-text-soft-400 mt-4 font-mono text-[11px]">
            Référence : {error.digest}
          </p>
        )}
      </div>
    </main>
  );
}
